/**
 * feat-138 — CLIENT-SIDE derivation, FOR DISPLAY ONLY (ADR-0001 D2).
 *
 * The backend is the source of truth for the wire blobs (it derives both
 * `challenge.status` rows). This module re-implements the SAME D2 fan-out purely
 * so the editor's two live previews render EXACTLY what each row will look like
 * on-device — a deliberate temporary replica, NOT a shared component, NOT sent
 * over the wire.
 *
 * Given the ONE logical model, `deriveRow(logical, i)` produces the derived
 * `status` blob for team `i`'s location: own header, own team first, is_you
 * flipped. This matches what the Flutter `PairingChallenge` parser reads.
 */
import type { PairingLogical, PaceStatus } from '../../../services/pairingChallengeService';

export interface DerivedTeam {
  location_id: string;
  name: string;
  is_you: boolean;
  percent_to_goal: number;
  pace_status: PaceStatus;
  pace_label: string;
  bar_color: string;
  avatar_url: string;
  avatar_placeholder: string;
}

export interface DerivedRow {
  schema_version: 2;
  type: 'head_to_head_pairing';
  title: string;
  campaign: { start: string; end: string };
  header: {
    yesterday_count: number;
    today_goal: number;
    today_goal_caption: string;
  };
  bars_title: string;
  pace_percent: number;
  teams: DerivedTeam[];
}

/**
 * Derive the blob for the location at `ownIndex` (0 = One California, 1 = 580).
 * Own location leads the `teams` array with `is_you: true`; the opponent
 * follows with `is_you: false` (D2/D3). Shared display fields are identical in
 * both appearances — only `is_you` and array position differ.
 */
export function deriveRow(logical: PairingLogical, ownIndex: number): DerivedRow {
  const own = logical.teams[ownIndex];
  const opponent = logical.teams[ownIndex === 0 ? 1 : 0];

  const toDerivedTeam = (
    t: PairingLogical['teams'][number],
    isYou: boolean,
  ): DerivedTeam => ({
    location_id: t.location_id,
    name: t.name,
    is_you: isYou,
    percent_to_goal: t.percent_to_goal,
    pace_status: t.pace_status,
    pace_label: t.pace_label,
    bar_color: t.bar_color,
    avatar_url: t.avatar_url,
    avatar_placeholder: t.avatar_placeholder,
  });

  return {
    schema_version: 2,
    type: 'head_to_head_pairing',
    title: logical.title,
    campaign: { start: logical.campaign.start, end: logical.campaign.end },
    header: {
      // L's OWN header numbers only (D2 step 2).
      yesterday_count: own.yesterday_count,
      today_goal: own.today_goal,
      today_goal_caption: logical.today_goal_caption,
    },
    bars_title: logical.bars_title,
    pace_percent: logical.pace_percent,
    teams: [toDerivedTeam(own, true), toDerivedTeam(opponent, false)],
  };
}
